import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, FileCode, DollarSign, Building2, TrendingUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useBrand } from "@/config/brands";

interface MedicalCodeSearchProps {
  onSearch?: (query: string, type: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
}

interface CodeResult {
  code: string;
  codeType: string;
  description: string;
  avgPrice?: number;
  medianPrice?: number;
  totalHospitals?: number;
  minPrice?: number;
  maxPrice?: number;
  consumerName?: string;
  summaryDescription?: string;
  variations?: number;
}

export function MedicalCodeSearch({
  onSearch,
  placeholder,
  autoFocus = false,
}: MedicalCodeSearchProps) {
  const { t } = useTranslation(['search', 'common']);
  const [query, setQuery] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [isLoading, setIsLoading] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const brand = useBrand();

  // Search for medical codes across all tables
  const { data: codeResults = [], isLoading: isSearching } = useQuery({
    queryKey: ["medical-code-search", query],
    queryFn: async () => {
      if (!query.trim() || query.length < 2) return [];

      const results: CodeResult[] = [];
      const searchTerm = query.trim();

      // Search CPT codes (from procedures table, avoiding duplicates)
      const { data: cptData } = await supabase
        .from("procedures")
        .select("code, description, llm_quick_name, llm_summary_description")
        .eq("code_type", "CPT")
        .or(`code.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,llm_quick_name.ilike.%${searchTerm}%`)
        .limit(10);

      if (cptData && cptData.length > 0) {
        // Deduplicate by code
        const uniqueCptCodes = new Map();
        cptData.forEach(item => {
          if (!uniqueCptCodes.has(item.code)) {
            uniqueCptCodes.set(item.code, item);
          }
        });

        // Get aggregated pricing data for unique CPT codes
        const cptCodes = Array.from(uniqueCptCodes.keys());
        const { data: cptPricing } = await supabase
          .from("cpt_pricing_averages")
          .select("cpt_code, avg_gross_charge, median_gross_charge, min_gross_charge, max_gross_charge, total_hospitals, setting_type, normalized_unit")
          .in("cpt_code", cptCodes);

        // Aggregate pricing by code
        const aggregatedPricing = new Map();
        cptPricing?.forEach(p => {
          if (!aggregatedPricing.has(p.cpt_code)) {
            aggregatedPricing.set(p.cpt_code, {
              variations: new Set(),
              totalHospitals: 0,
              avgPrices: [],
              medianPrices: [],
              minPrice: p.min_gross_charge || 0,
              maxPrice: p.max_gross_charge || 0
            });
          }
          const agg = aggregatedPricing.get(p.cpt_code);
          const variationKey = `${p.setting_type || 'unspecified'}_${p.normalized_unit || 'other'}`;
          agg.variations.add(variationKey);
          agg.totalHospitals += p.total_hospitals || 0;
          if (p.avg_gross_charge) agg.avgPrices.push(p.avg_gross_charge);
          if (p.median_gross_charge) agg.medianPrices.push(p.median_gross_charge);
          if (p.min_gross_charge && p.min_gross_charge < agg.minPrice) agg.minPrice = p.min_gross_charge;
          if (p.max_gross_charge && p.max_gross_charge > agg.maxPrice) agg.maxPrice = p.max_gross_charge;
        });

        Array.from(uniqueCptCodes.values()).slice(0, 3).forEach((item) => {
          const agg = aggregatedPricing.get(item.code);
          results.push({
            code: item.code,
            codeType: "CPT",
            description: item.llm_quick_name || item.description || "",
            summaryDescription: item.llm_summary_description || "",
            avgPrice: agg ? (agg.avgPrices.reduce((a, b) => a + b, 0) / agg.avgPrices.length) : 0,
            medianPrice: agg ? (agg.medianPrices.reduce((a, b) => a + b, 0) / agg.medianPrices.length) : 0,
            minPrice: agg?.minPrice || 0,
            maxPrice: agg?.maxPrice || 0,
            totalHospitals: agg?.totalHospitals || 0,
            variations: agg?.variations.size || 0,
            consumerName: item.llm_quick_name || "",
          });
        });
      }

      // Search DRG codes
      const { data: drgData } = await supabase
        .from("drg_codes")
        .select("drg_code, description, llm_quick_name")
        .or(`drg_code.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,llm_quick_name.ilike.%${searchTerm}%`)
        .limit(3);

      if (drgData && drgData.length > 0) {
        // Get pricing data for DRG codes
        const drgCodes = drgData.map(d => d.drg_code);
        const { data: drgPricing } = await supabase
          .from("drg_pricing_averages")
          .select("drg_code, avg_gross_charge, min_gross_charge, max_gross_charge, total_hospitals")
          .in("drg_code", drgCodes)
          .order("total_hospitals", { ascending: false });

        const pricingMap = new Map(drgPricing?.map(p => [p.drg_code, p]) || []);

        drgData.forEach((item) => {
          const pricing = pricingMap.get(item.drg_code);
          results.push({
            code: item.drg_code,
            codeType: "DRG",
            description: item.llm_quick_name || item.description || "",
            avgPrice: pricing?.avg_gross_charge || 0,
            minPrice: pricing?.min_gross_charge || 0,
            maxPrice: pricing?.max_gross_charge || 0,
            totalHospitals: pricing?.total_hospitals || 0,
            consumerName: item.llm_quick_name || "",
          });
        });
      }

      // Search HCPCS codes (from procedures table, avoiding duplicates)
      const { data: hcpcsData } = await supabase
        .from("procedures")
        .select("code, description, llm_quick_name, llm_summary_description")
        .eq("code_type", "HCPCS")
        .or(`code.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,llm_quick_name.ilike.%${searchTerm}%`)
        .limit(10);

      if (hcpcsData && hcpcsData.length > 0) {
        // Deduplicate by code
        const uniqueHcpcsCodes = new Map();
        hcpcsData.forEach(item => {
          if (!uniqueHcpcsCodes.has(item.code)) {
            uniqueHcpcsCodes.set(item.code, item);
          }
        });

        // Get aggregated pricing data for unique HCPCS codes
        const hcpcsCodes = Array.from(uniqueHcpcsCodes.keys());
        const { data: hcpcsPricing } = await supabase
          .from("hcpcs_pricing_averages")
          .select("hcpcs_code, avg_gross_charge, median_gross_charge, min_gross_charge, max_gross_charge, total_hospitals, setting_type, normalized_unit")
          .in("hcpcs_code", hcpcsCodes);

        // Aggregate pricing by code
        const aggregatedPricing = new Map();
        hcpcsPricing?.forEach(p => {
          if (!aggregatedPricing.has(p.hcpcs_code)) {
            aggregatedPricing.set(p.hcpcs_code, {
              variations: new Set(),
              totalHospitals: 0,
              avgPrices: [],
              medianPrices: [],
              minPrice: p.min_gross_charge || 0,
              maxPrice: p.max_gross_charge || 0
            });
          }
          const agg = aggregatedPricing.get(p.hcpcs_code);
          const variationKey = `${p.setting_type || 'unspecified'}_${p.normalized_unit || 'other'}`;
          agg.variations.add(variationKey);
          agg.totalHospitals += p.total_hospitals || 0;
          if (p.avg_gross_charge) agg.avgPrices.push(p.avg_gross_charge);
          if (p.median_gross_charge) agg.medianPrices.push(p.median_gross_charge);
          if (p.min_gross_charge && p.min_gross_charge < agg.minPrice) agg.minPrice = p.min_gross_charge;
          if (p.max_gross_charge && p.max_gross_charge > agg.maxPrice) agg.maxPrice = p.max_gross_charge;
        });

        Array.from(uniqueHcpcsCodes.values()).slice(0, 3).forEach((item) => {
          const agg = aggregatedPricing.get(item.code);
          results.push({
            code: item.code,
            codeType: "HCPCS",
            description: item.llm_quick_name || item.description || "",
            summaryDescription: item.llm_summary_description || "",
            avgPrice: agg ? (agg.avgPrices.reduce((a, b) => a + b, 0) / agg.avgPrices.length) : 0,
            medianPrice: agg ? (agg.medianPrices.reduce((a, b) => a + b, 0) / agg.medianPrices.length) : 0,
            minPrice: agg?.minPrice || 0,
            maxPrice: agg?.maxPrice || 0,
            totalHospitals: agg?.totalHospitals || 0,
            variations: agg?.variations.size || 0,
            consumerName: item.llm_quick_name || "",
          });
        });
      }

      // Search Revenue codes (from procedures table with code_type='REV')
      const { data: revData } = await supabase
        .from("procedures")
        .select("code, description, llm_quick_name")
        .eq("code_type", "REV")
        .or(`code.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,llm_quick_name.ilike.%${searchTerm}%`)
        .limit(10);

      if (revData && revData.length > 0) {
        // Deduplicate by code
        const uniqueRevCodes = new Map();
        revData.forEach(item => {
          if (!uniqueRevCodes.has(item.code)) {
            uniqueRevCodes.set(item.code, item);
          }
        });

        // Get pricing data for unique Revenue codes
        const revCodes = Array.from(uniqueRevCodes.keys());
        const { data: revPricing } = await supabase
          .from("revenue_pricing_averages")
          .select("revenue_code, avg_charge, min_charge, max_charge, total_hospitals")
          .in("revenue_code", revCodes)
          .order("total_hospitals", { ascending: false });

        const pricingMap = new Map(revPricing?.map(p => [p.revenue_code, p]) || []);

        Array.from(uniqueRevCodes.values()).slice(0, 2).forEach((item) => {
          const pricing = pricingMap.get(item.code);
          results.push({
            code: item.code,
            codeType: "REV",
            description: item.llm_quick_name || item.description || "",
            avgPrice: pricing?.avg_charge || 0,
            minPrice: pricing?.min_charge || 0,
            maxPrice: pricing?.max_charge || 0,
            totalHospitals: pricing?.total_hospitals || 0,
            consumerName: item.llm_quick_name || "",
          });
        });
      }

      // Search Drug codes
      const { data: drugData } = await supabase
        .from("drug_codes")
        .select("drug_code, drug_name, generic_name, llm_quick_name")
        .or(`drug_code.ilike.%${searchTerm}%,drug_name.ilike.%${searchTerm}%,generic_name.ilike.%${searchTerm}%,llm_quick_name.ilike.%${searchTerm}%`)
        .limit(3);

      if (drugData && drugData.length > 0) {
        // Get pricing data for Drug codes (try both drug and NDC tables)
        const drugCodes = drugData.map(d => d.drug_code);

        // Try drug_pricing_averages first
        const { data: drugPricing } = await supabase
          .from("drug_pricing_averages")
          .select("drug_code, avg_charge, min_charge, max_charge, total_hospitals")
          .in("drug_code", drugCodes)
          .order("total_hospitals", { ascending: false });

        // Also try ndc_pricing_averages as fallback
        const { data: ndcPricing } = await supabase
          .from("ndc_pricing_averages")
          .select("ndc_code, avg_charge, min_charge, max_charge, total_hospitals")
          .in("ndc_code", drugCodes)
          .order("total_hospitals", { ascending: false });

        const drugPricingMap = new Map(drugPricing?.map(p => [p.drug_code, p]) || []);
        const ndcPricingMap = new Map(ndcPricing?.map(p => [p.ndc_code, p]) || []);

        drugData.forEach((item) => {
          const pricing = drugPricingMap.get(item.drug_code) ||
                        (ndcPricingMap.has(item.drug_code) ? {
                          avg_charge: ndcPricingMap.get(item.drug_code)?.avg_charge,
                          min_charge: ndcPricingMap.get(item.drug_code)?.min_charge,
                          max_charge: ndcPricingMap.get(item.drug_code)?.max_charge,
                          total_hospitals: ndcPricingMap.get(item.drug_code)?.total_hospitals,
                        } : null);

          results.push({
            code: item.drug_code,
            codeType: "RX",
            description: item.llm_quick_name || item.drug_name || item.generic_name || "",
            avgPrice: pricing?.avg_charge || 0,
            minPrice: pricing?.min_charge || 0,
            maxPrice: pricing?.max_charge || 0,
            totalHospitals: pricing?.total_hospitals || 0,
            consumerName: item.llm_quick_name || "",
          });
        });
      }

      // Search Supply codes (limit to 2 to keep total results manageable)
      const { data: supplyData } = await supabase
        .from("supply_codes")
        .select("supply_code, supply_description, llm_quick_name")
        .or(`supply_code.ilike.%${searchTerm}%,supply_description.ilike.%${searchTerm}%,llm_quick_name.ilike.%${searchTerm}%`)
        .limit(2);

      if (supplyData && supplyData.length > 0) {
        // Get pricing data for Supply codes
        const supplyCodes = supplyData.map(d => d.supply_code);
        const { data: supplyPricing } = await supabase
          .from("supply_pricing_averages")
          .select("supply_code, avg_charge, min_charge, max_charge, total_hospitals")
          .in("supply_code", supplyCodes)
          .order("total_hospitals", { ascending: false });

        const pricingMap = new Map(supplyPricing?.map(p => [p.supply_code, p]) || []);

        supplyData.forEach((item) => {
          const pricing = pricingMap.get(item.supply_code);
          results.push({
            code: item.supply_code,
            codeType: "SUP",
            description: item.llm_quick_name || item.supply_description || "",
            avgPrice: pricing?.avg_charge || 0,
            minPrice: pricing?.min_charge || 0,
            maxPrice: pricing?.max_charge || 0,
            totalHospitals: pricing?.total_hospitals || 0,
            consumerName: item.llm_quick_name || "",
          });
        });
      }

      return results; // Already limited by individual queries
    },
    enabled: query.length >= 2,
    staleTime: 30000,
  });

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || codeResults.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % codeResults.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev <= 0 ? codeResults.length - 1 : prev - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && codeResults[selectedIndex]) {
          handleSelectCode(codeResults[selectedIndex]);
        } else {
          handleSubmit(e);
        }
        break;
      case "Escape":
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleSelectCode = (code: CodeResult) => {
    setQuery(code.code);
    setShowDropdown(false);
    setSelectedIndex(-1);

    // Navigate to the appropriate page based on code type
    let routePrefix = code.codeType.toLowerCase();
    if (code.codeType === "DRG") {
      routePrefix = "msdrg";
    } else if (code.codeType === "RX") {
      routePrefix = "drug";
    } else if (code.codeType === "SUP") {
      routePrefix = "supply";
    }

    navigate(`/${routePrefix}/${code.code}`);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      setIsLoading(true);
      setShowDropdown(false);
      if (onSearch) {
        onSearch(query.trim(), "code");
      } else {
        navigate(`/search?q=${encodeURIComponent(query.trim())}&type=code`);
      }
      setTimeout(() => setIsLoading(false), 500);
    }
  };

  // Show/hide dropdown
  useEffect(() => {
    if (query.length >= 2 && codeResults.length > 0) {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
      setSelectedIndex(-1);
    }
  }, [codeResults, query]);

  // Handle clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Format price for display
  const formatPrice = (price: number) => {
    if (!price) return "N/A";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  // Get code type badge color (matching analytics section)
  const getCodeTypeColor = (codeType: string) => {
    switch (codeType) {
      case "CPT":
        return "bg-blue-600 text-white";
      case "DRG":
        return "bg-purple-600 text-white";
      case "REV":
        return "bg-yellow-600 text-white";
      case "RX":
        return "bg-red-600 text-white";
      case "HCPCS":
        return "bg-cyan-600 text-white";
      case "SUP":
        return "bg-amber-600 text-white";
      default:
        return "bg-gray-600 text-white";
    }
  };

  return (
    <div className="relative w-full">
      <form onSubmit={handleSubmit} className="relative">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <FileCode className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={placeholder || t('search:codeSearch.placeholder')}
              autoFocus={autoFocus}
              className="w-full pl-10 pr-4 py-3 terminal-input text-base"
            />
          </div>
          <Button
            type="submit"
            disabled={isLoading}
            className={`${brand.searchButtonBg} ${brand.searchButtonHover} ${brand.textColor} font-mono px-3 sm:px-4`}
          >
            {isLoading ? (
              <span className="loading-dots text-xs sm:text-sm">{t('search:loading.searching')}</span>
            ) : (
              <>
                <Search className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('common:actions.search')}</span>
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Autocomplete dropdown */}
      {showDropdown && codeResults.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-[9999] w-full mt-2 bg-black/95 backdrop-blur-sm border border-green-500/20 rounded-lg shadow-lg overflow-hidden"
        >
          <div className="max-h-[400px] overflow-y-auto">
            {codeResults.map((result, index) => (
              <div
                key={`${result.codeType}-${result.code}`}
                className={`p-4 cursor-pointer border-b border-green-500/10 hover:bg-green-500/10 transition-colors ${
                  index === selectedIndex ? "bg-green-500/20" : ""
                }`}
                onClick={() => handleSelectCode(result)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`${getCodeTypeColor(result.codeType)} text-xs font-bold`}>
                        {result.codeType}
                      </Badge>
                      <span className="text-green-400 font-mono font-semibold">
                        {result.code}
                      </span>
                    </div>
                    <div className="text-white font-medium mb-1">
                      {result.consumerName || result.description}
                    </div>
                    {result.summaryDescription && (
                      <div className="text-gray-400 text-xs line-clamp-2">
                        {result.summaryDescription}
                      </div>
                    )}
                  </div>
                  <div className="text-right ml-4">
                    {result.avgPrice && result.avgPrice > 0 ? (
                      <>
                        <div className="text-green-400 font-mono font-semibold text-lg">
                          {formatPrice(result.avgPrice)}
                        </div>
                        <div className="flex flex-col gap-1">
                          {result.totalHospitals && result.totalHospitals > 0 && (
                            <div className="text-xs text-gray-400 flex items-center gap-1 justify-end">
                              <Building2 className="w-3 h-3" />
                              {t('search:results.totalHospitals', { count: result.totalHospitals })}
                            </div>
                          )}
                          {result.variations && result.variations > 1 && (
                            <div className="text-xs text-gray-400">
                              {result.variations} {t('search:codeSearch.variations')}
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="text-gray-500 text-sm">{t('search:codeSearch.noPricingData')}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}